import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Trash2 } from 'lucide-react'
import { besprechungExtra, type BesprechungExtra } from '@symphenia/shared-types'
import { feldKlasse } from '@/lib/speichern'
import type { ErpEinstellungenProps } from '../registry'

/**
 * AP-49 11.1 — **Einstellungen der Projektbesprechung**: die Besprechungsarten des Mandanten (BS-2) und
 * die Einspruchsfrist mit ihrem Text (BS-10). Gespeichert wird die Einstellung als Ganzes beim Verlassen
 * eines Feldes; die leere letzte Zeile legt eine Art an. Eine Art umbenennen beginnt eine neue Serie — die
 * bisherigen Protokolle behalten ihre Art.
 */
export function BesprechungEinstellungen({ extra, speichere }: ErpEinstellungenProps) {
  const { t } = useTranslation()
  const [stand, setStand] = useState<BesprechungExtra>(() => besprechungExtra(extra))
  const [neu, setNeu] = useState('')

  const schreibe = (n: BesprechungExtra) => {
    const sauber = { ...n, arten: n.arten.map((a) => a.trim()).filter(Boolean) }
    if (!sauber.arten.length) return
    setStand(sauber)
    void speichere(sauber as unknown as Record<string, unknown>)
  }

  return (
    <div className="space-y-3 border-t pt-3">
      <div className="space-y-1">
        <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t('siteDocs.meeting.types')}</p>
        <p className="text-xs text-muted-foreground">{t('siteDocs.meeting.typesHelp')}</p>
        <ul className="space-y-1">
          {stand.arten.map((a, i) => (
            <li key={i} className="flex items-center gap-2">
              <input
                className={feldKlasse}
                value={a}
                aria-label={t('siteDocs.meeting.type')}
                onChange={(e) => setStand({ ...stand, arten: stand.arten.map((x, j) => (j === i ? e.target.value : x)) })}
                onBlur={() => schreibe(stand)}
              />
              {stand.arten.length > 1 && (
                <button type="button" className="rounded p-1 text-muted-foreground hover:bg-destructive/10 hover:text-destructive" aria-label={t('common.delete')} onClick={() => schreibe({ ...stand, arten: stand.arten.filter((_, j) => j !== i) })}>
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              )}
            </li>
          ))}
          <li>
            <input
              className={feldKlasse}
              value={neu}
              placeholder={t('siteDocs.meeting.newType')}
              aria-label={t('siteDocs.meeting.newType')}
              onChange={(e) => setNeu(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') (e.target as HTMLInputElement).blur()
              }}
              onBlur={() => {
                if (!neu.trim()) return
                schreibe({ ...stand, arten: [...stand.arten, neu.trim()] })
                setNeu('')
              }}
            />
          </li>
        </ul>
      </div>
      <div className="grid gap-2 sm:grid-cols-[10rem_1fr]">
        <label className="text-sm">
          <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t('siteDocs.meeting.objectionDays')}</span>
          <input
            className={`${feldKlasse} text-right`}
            inputMode="numeric"
            value={String(stand.einspruchTage)}
            onChange={(e) => setStand({ ...stand, einspruchTage: Math.max(0, Math.min(365, Number(e.target.value.replace(/\D/g, '')) || 0)) })}
            onBlur={() => schreibe(stand)}
          />
        </label>
        <label className="text-sm">
          <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t('siteDocs.meeting.objectionText')}</span>
          <textarea className={`${feldKlasse} min-h-16`} value={stand.einspruchText} onChange={(e) => setStand({ ...stand, einspruchText: e.target.value })} onBlur={() => schreibe(stand)} />
          <span className="text-xs text-muted-foreground">{t('siteDocs.meeting.objectionTextHelp')}</span>
        </label>
      </div>
    </div>
  )
}
